import React, { useEffect, useRef } from 'react'
import { ActivityIndicator, Linking } from 'react-native'

import { useNavigation, useRoute } from '@react-navigation/native'
import { publishEvent } from '@zet/analytics'
import { colors as zetColors } from '@zet/design-system'
import { BTN, Icon } from '@zet/design-system/index'
import { Stack } from '@zet/design-system/layout'
import Pressable from '@zet/design-system/pressable'
import { useModal } from 'react-native-modalfy'

import { useShareLinkWeb } from '../../../api/shareLinkWeb/hooks'
import { useApiAction } from '../../../api/useApiAction'
import { brandsI18n } from '../../../localization'
import { partnerIdMap } from '../../../utils/partnerIdMap'

interface Props {
  type: string
  title: string
  icon?: string
  loading?: boolean
  action: string
  redirectUrl?: string
  nextStateRedirect?: string
  disabled?: boolean
  payload?: any
  eventValue?: string
}

// TODO: Get url from backend
const uri =
  'https://onecode-asset.s3.ap-south-1.amazonaws.com/Brand_Logo_PNGs/202.png'

const ActionButton: React.FC<Props> = (props) => {
  const {
    type,
    title,
    icon,
    loading,
    action,
    nextStateRedirect,
    disabled,
    redirectUrl,
    payload = {},
    eventValue,
  } = props
  const { stateManagePost, data, hash } = useApiAction({
    skipInitFetch: true,
  })

  const { name: routeName } = useRoute()

  const isLOS = routeName === 'BrandsLOS' ? true : false
  const navigation = useNavigation()

  const modalShouldOpen = useRef(false)

  const { openModal } = useModal()

  const {
    isLoading,
    data: linkData,
    refetch,
    isSuccess,
  } = useShareLinkWeb(
    data?.metadata?.partnerId,
    data?.metadata?.customerId,
    hash && encodeURIComponent(hash)
  )

  useEffect(() => {
    if (isSuccess && linkData && modalShouldOpen.current) {
      openModal('WebFlowApplyModal', {
        brandName: brandsI18n.t('axis.shareModalTitle'),
        brandLogo: uri,
        customerPhoneNumber: data?.metadata?.phoneNumber,
        brandCategoryId: '265',
        whatsappShareTemplate: encodeURIComponent(
          brandsI18n.t('axis.whatsappShareTemplate', {
            brandName: 'Axis',
            applicationLink: linkData?.url,
          })
        ),
        modalType: 'apply',
      })
      modalShouldOpen.current = false
    }
  }, [data?.metadata?.phoneNumber, isSuccess, linkData, openModal])

  const onPress = () => {
    publishEvent('click_action', {
      item_name: 'CC_FULL_INTEGRATION',
      cta_name: title,
      page_name: data?.state,
      lead_ID: data?.metadata?.leadId,
      customer_ID: data?.metadata?.customerId,
      partner_ID: data?.metadata?.partnerId,
      brand_name: partnerIdMap?.[String(data?.metadata?.partnerId)] ?? '',
      brand_category: '15',
    })

    if (action === 'STATE_MACHINE_POST') {
      stateManagePost({
        forceRefetch: true,
        payload,
        extras: {
          event: eventValue,
        },
      })
    } else if (action === 'KYC_SHARE_LINK') {
      modalShouldOpen.current = true
      refetch()
    } else if (action === 'RETRY_SUBMIT_STATUS_CHECK') {
      stateManagePost()
    } else if (action === 'REDIRECT_NEXT_STATE' && redirectUrl) {
      window.open(redirectUrl, '_self')
    } else if (action === 'REDIRECT_NEXT_STATE' && nextStateRedirect) {
      navigation.replace(isLOS ? 'BrandsLOS' : 'BrandsCC', {
        hash,
        state: nextStateRedirect,
      })
    } else if (action === 'REDIRECT_LEAD_DETAILS') {
      Linking.openURL(`zetapp://lead/details?leadId=${data?.metadata?.leadId}`)
    }
  }

  return (
    <Stack className="mx-3 my-2 flex-col" style={{ bottom: 0 }}>
      {type === 'outlined' ? (
        <Pressable
          className={`${
            disabled ? 'border-primary-100' : 'border-grays-40'
          } h-7 flex-row items-center justify-center rounded-md border`}
          onPress={onPress}
          disabled={disabled}
        >
          {!!icon && (
            <Icon
              type={icon}
              color={disabled ? zetColors.grays[50] : zetColors.primary[100]}
              className="mr-2"
            />
          )}
          <BTN className={disabled ? 'text-grays-50' : 'text-primary-100'}>
            {title}
          </BTN>
        </Pressable>
      ) : loading ? (
        <Pressable
          disabled={disabled}
          className="h-7 flex-row items-center justify-center rounded-md bg-gray-400"
          onPress={onPress}
        >
          <ActivityIndicator
            size="small"
            color={disabled ? zetColors.neutral['white100'] : '#0000ff'}
          />
          <BTN className="text-neutral-white100 ml-2">{title}</BTN>
        </Pressable>
      ) : (
        <Pressable
          disabled={disabled}
          className={`${
            disabled
              ? 'bg-grays-40'
              : type === 'secondary'
              ? 'bg-primary-10 px-3'
              : 'bg-primary-100'
          } h-7 flex-row items-center justify-center rounded-md`}
          onPress={onPress}
        >
          {!!icon && (
            <Icon
              type={icon}
              color={
                type === 'secondary'
                  ? zetColors.primary[100]
                  : zetColors.neutral['white100']
              }
              className="mr-2"
            />
          )}
          <BTN
            className={` ${
              type === 'secondary'
                ? 'text-primary-100'
                : 'text-neutral-white100'
            }`}
          >
            {title}
          </BTN>
        </Pressable>
      )}
    </Stack>
  )
}

export default ActionButton
